<template>
  <v-row data-testid="business-hours">
    <v-col cols="12" sm="4">
      <v-text-field
        v-model="start"
        :rules="[isBusinessHour]"
        label="Business Start Time"
        placeholder="HH:mm"
        hint="24-hour clock, e.g. 08:00. Leave blank to test a day the site is closed."
        persistent-hint
        persistent-placeholder
        variant="outlined"
        density="compact"
        data-testid="business-start-time"
      />
    </v-col>
    <v-col cols="12" sm="4">
      <v-text-field
        v-model="end"
        :rules="[isBusinessHour]"
        label="Business End Time"
        placeholder="HH:mm"
        hint="24-hour clock, e.g. 18:30. Leave blank to test a day the site is closed."
        persistent-hint
        persistent-placeholder
        variant="outlined"
        density="compact"
        data-testid="business-end-time"
      />
    </v-col>
  </v-row>
</template>
<script>
import {
  hasValue,
  isValid24HourFormat,
} from '@/Core.Patterns/Factory/validationFactory.ts';

// A replayed run carries the recorded TimeSpan, which serialises with seconds
// ("07:00:00"); the field -- and what we send back -- is HH:mm. Business hours
// are always set on the minute, so the seconds are noise.
const toFieldValue = (value) => {
  if (!hasValue(value)) return '';

  const [hours, minutes] = String(value).split(':');
  if (minutes === undefined) return String(value);

  return `${hours}:${minutes}`;
};

// SiteStartTime and SiteEndTime belong to the base Input every strategy input
// derives from. Core fills them from the site's business hours for the day of
// the run; these fields let a test run pretend the site opens and closes at a
// different time, which is what drives Is Open Today / During Business Hours.
//
// Both are TimeSpan? on the Core side, so they travel as 24-hour strings and
// blank means "no business hours today". The editor that hosts this component
// binds them straight onto its own input data, so they ride the same
// update:model the rest of the input's values already go out on.
export default {
  name: 'BusinessHoursInput',

  props: {
    startTime: {
      type: String,
      default: null,
    },
    endTime: {
      type: String,
      default: null,
    },
  },

  emits: ['update:startTime', 'update:endTime'],

  data() {
    return {
      start: toFieldValue(this.startTime),
      end: toFieldValue(this.endTime),
    };
  },

  watch: {
    // The host binds these with v-model, so an edit comes straight back as a
    // prop. Re-seed only on a real change, otherwise the echo of our own emit
    // would fight the value being typed.
    startTime(value) {
      if (toFieldValue(value) !== this.start) this.start = toFieldValue(value);
    },

    endTime(value) {
      if (toFieldValue(value) !== this.end) this.end = toFieldValue(value);
    },

    // Null rather than '' for a cleared field, so it overrides the value a
    // replayed run recorded instead of leaving it in place. Core reads the null
    // back as "the site was closed that day".
    start(value) {
      this.$emit('update:startTime', hasValue(value) ? value : null);
    },

    end(value) {
      this.$emit('update:endTime', hasValue(value) ? value : null);
    },
  },

  methods: {
    isBusinessHour(value) {
      // Blank is a meaningful value here -- a site with no hours for the day --
      // so only a non-empty entry has to look like a time.
      if (!hasValue(value)) return true;
      return isValid24HourFormat(value) || 'Use a 24-hour time, e.g. 08:00';
    },
  },
};
</script>
